import { useQuery } from "@tanstack/react-query";

import authService, { TOKEN_TYPES } from "../services/authService";
import { ApiError } from "./FetchHook";
import { useScopeParams } from "./useScopeParams";

export const PLOT_DATA_PAGE_SIZE = 2000;

const ABSOLUTE_URL_PATTERN = /^https?:\/\//i;

const trimSlashes = (value: string) => value.replace(/^\/+|\/+$/g, "");
const trimLeadingSlashes = (value: string) => value.replace(/^\/+/, "");

const buildPlotRequestUrl = ({
  baseUrl,
  endpointPrefix,
  id,
  endpointSuffix,
}: {
  baseUrl: string;
  endpointPrefix?: string;
  id?: string;
  endpointSuffix?: string;
}) => {
  const normalizedPrefix = endpointPrefix ? trimSlashes(endpointPrefix) : "";
  const normalizedId = id ? trimSlashes(id) : "";
  const normalizedSuffix = endpointSuffix
    ? trimLeadingSlashes(endpointSuffix)
    : "";

  if (endpointPrefix && ABSOLUTE_URL_PATTERN.test(endpointPrefix)) {
    return [normalizedPrefix, normalizedId, normalizedSuffix]
      .filter(Boolean)
      .join("/");
  }

  return [baseUrl, normalizedPrefix, normalizedId, normalizedSuffix]
    .filter(Boolean)
    .join("/");
};

type PlotRequestBody = Record<string, any> & {
  pagination?: {
    page: number;
    page_size: number;
  };
};

const throwIfAborted = (signal?: AbortSignal) => {
  if (!signal?.aborted) {
    return;
  }

  if (typeof signal.throwIfAborted === "function") {
    signal.throwIfAborted();
    return;
  }

  throw new DOMException("The operation was aborted.", "AbortError");
};

type PaginatedPlotResponse = Record<string, any> & {
  count?: number;
  results?: any[];
  next?: string | null;
  previous?: string | null;
};

type PlotFetchArgs = {
  scope: string;
  id?: string;
  endpointPrefix?: string;
  endpointSuffix: string;
  body: PlotRequestBody;
  signal?: AbortSignal;
};

type FetchAllPlotPagesArgs = PlotFetchArgs & {
  pageSize?: number;
};

const postPlotPage = async ({
  scope,
  id,
  endpointPrefix,
  endpointSuffix,
  body,
  signal,
}: PlotFetchArgs): Promise<PaginatedPlotResponse> => {
  const token = authService.getToken(TOKEN_TYPES.ACCESS);
  const options: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  };

  if (token) {
    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  const prefix = endpointPrefix || `data/${scope}`;

  const response = await fetch(
    buildPlotRequestUrl({
      baseUrl: import.meta.env.VITE_API_URL,
      endpointPrefix: prefix,
      id,
      endpointSuffix,
    }),
    options,
  );

  if (!response.ok) {
    throw new ApiError(
      `HTTP error! status: ${response.status}`,
      response.status,
    );
  }

  return response.json();
};

export async function fetchAllPlotPages({
  scope,
  id,
  endpointPrefix,
  endpointSuffix,
  body,
  signal,
  pageSize = PLOT_DATA_PAGE_SIZE,
}: FetchAllPlotPagesArgs): Promise<PaginatedPlotResponse> {
  const firstPageBody = {
    ...body,
    pagination: {
      ...body.pagination,
      page: 1,
      page_size: pageSize,
    },
  };

  const firstResponse = await postPlotPage({
    scope,
    id,
    endpointPrefix,
    endpointSuffix,
    body: firstPageBody,
    signal,
  });

  const mergedResults = [...(firstResponse.results ?? [])];
  const hasKnownCount = typeof firstResponse.count === "number";
  const totalPages = hasKnownCount
    ? Math.max(1, Math.ceil(firstResponse.count! / pageSize))
    : undefined;

  let currentPage = 1;
  let lastResponse = firstResponse;

  while (true) {
    throwIfAborted(signal);

    const currentPageResults = lastResponse.results ?? [];
    const shouldStopByCount = totalPages ? currentPage >= totalPages : false;
    const shouldStopByPageSize =
      !totalPages && currentPageResults.length < pageSize;

    if (shouldStopByCount || shouldStopByPageSize) {
      break;
    }

    currentPage += 1;

    lastResponse = await postPlotPage({
      scope,
      id,
      endpointPrefix,
      endpointSuffix,
      body: {
        ...body,
        pagination: {
          ...body.pagination,
          page: currentPage,
          page_size: pageSize,
        },
      },
      signal,
    });

    mergedResults.push(...(lastResponse.results ?? []));

    if (!totalPages && (lastResponse.results ?? []).length < pageSize) {
      break;
    }
  }

  const mergedCount =
    typeof lastResponse.count === "number"
      ? lastResponse.count
      : typeof firstResponse.count === "number"
        ? firstResponse.count
        : mergedResults.length;

  return {
    ...firstResponse,
    count: mergedCount,
    next: null,
    previous: null,
    results: mergedResults,
  };
}

export const usePaginatedPlotFetch = ({
  endpointPrefix,
  endpointSuffix,
  body,
  enabled = true,
}: {
  endpointPrefix?: string;
  endpointSuffix: string;
  body: PlotRequestBody;
  enabled?: boolean;
}) => {
  const { scope, id } = useScopeParams();

  return useQuery({
    queryKey: [
      "paginated-plot-fetch",
      endpointPrefix,
      endpointSuffix,
      scope,
      id,
      body,
    ],
    queryFn: ({ signal }) =>
      fetchAllPlotPages({
        scope,
        id,
        endpointPrefix,
        endpointSuffix,
        body,
        signal,
      }),
    enabled,
    refetchOnWindowFocus: false,
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 1,
  });
};
